import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiCheckCircle, 
  FiClock, 
  FiUsers, 
  FiShield,
  FiTrendingUp,
  FiActivity,
  FiUserCheck,
  FiUserX,
  FiBarChart2,
  FiCalendar,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiRefreshCw,
  FiDownload,
  FiFilter
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';
import { 
  LineChart, 
  Line, 
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalVerifications: 1247,
    approved: 1189,
    pending: 42,
    rejected: 16,
    approvalRate: 95.3,
    growth: '+12.5%'
  });

  const [recentActivity, setRecentActivity] = useState([
    { id: 1, dssn: 'DLR-8K2P-X91M-4QZ7', status: 'approved', time: '2 min ago', institution: 'Liberia Bank', user: 'John Doe' },
    { id: 2, dssn: 'DLR-3M9N-K72L-1HB8', status: 'pending', time: '15 min ago', institution: 'Mobile Money Corp', user: 'Jane Smith' },
    { id: 3, dssn: 'DLR-5R7T-J44P-9XF2', status: 'approved', time: '1 hour ago', institution: 'Insurance Co.', user: 'Robert Johnson' },
    { id: 4, dssn: 'DLR-7K2M-P83Q-5WN6', status: 'rejected', time: '3 hours ago', institution: 'Telecom Liberia', user: 'Mary Williams' },
    { id: 5, dssn: 'DLR-9H4N-Q12R-7YT3', status: 'approved', time: '5 hours ago', institution: 'Bank of Liberia', user: 'David Brown' },
  ]);

  const [chartData] = useState([
    { day: 'Mon', verifications: 45, approved: 42, rejected: 3 },
    { day: 'Tue', verifications: 62, approved: 58, rejected: 4 },
    { day: 'Wed', verifications: 78, approved: 74, rejected: 4 },
    { day: 'Thu', verifications: 53, approved: 49, rejected: 4 },
    { day: 'Fri', verifications: 89, approved: 85, rejected: 4 },
    { day: 'Sat', verifications: 34, approved: 32, rejected: 2 },
    { day: 'Sun', verifications: 41, approved: 39, rejected: 2 },
  ]);

  const [pieData] = useState([
    { name: 'Approved', value: 1189, color: '#10b981' },
    { name: 'Pending', value: 42, color: '#f59e0b' },
    { name: 'Rejected', value: 16, color: '#ef4444' },
  ]);

  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const statCards = [
    { 
      title: 'Total Verifications', 
      value: stats.totalVerifications.toLocaleString(), 
      icon: <FiUsers />, 
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      trend: stats.growth,
      trendUp: true,
      subtitle: 'All time verifications'
    },
    { 
      title: 'Approved', 
      value: stats.approved.toLocaleString(), 
      icon: <FiCheckCircle />, 
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      trend: '+8.2%',
      trendUp: true,
      subtitle: 'Successfully verified'
    },
    { 
      title: 'Pending Consent', 
      value: stats.pending.toLocaleString(), 
      icon: <FiClock />, 
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      trend: '-2.1%',
      trendUp: false,
      subtitle: 'Awaiting approval'
    },
    { 
      title: 'Rejected', 
      value: stats.rejected.toLocaleString(), 
      icon: <FiShield />, 
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      trend: '-5.3%',
      trendUp: false,
      subtitle: 'Declined requests'
    },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'rejected': return '#ef4444';
      default: return '#6c63ff';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <FiCheckCircle />;
      case 'pending': return <FiClock />;
      case 'rejected': return <FiUserX />;
      default: return <FiShield />;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-page">
      {/* AI Background */}
      <div className="dashboard-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="dashboard-container">
        {/* Page Header */}
        <div className="page-header glass">
          <div className="header-content">
            <div className="title-section">
              <div className="badge">
                <HiOutlineSparkles />
                <span>Analytics Dashboard</span>
              </div>
              <h1>Verification Overview</h1>
              <p>Real-time insights into identity verification activities</p>
            </div>
            <div className="header-stats">
              <div className="mini-stat">
                <FiCalendar />
                <span>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="mini-stat">
                <FiBarChart2 />
                <span>Live Monitoring</span>
              </div>
              <button onClick={handleRefresh} className="refresh-btn" disabled={isRefreshing}>
                <FiRefreshCw className={isRefreshing ? 'spinning' : ''} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          {statCards.map((stat, index) => (
            <motion.div 
              key={index} 
              className="stat-card glass"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="stat-icon-wrapper" style={{ background: stat.gradient }}>
                {stat.icon}
              </div>
              <div className="stat-info">
                <h3>{stat.value}</h3>
                <p>{stat.title}</p>
                <span className="stat-subtitle">{stat.subtitle}</span>
                <div className={`stat-trend ${stat.trendUp ? 'up' : 'down'}`}>
                  {stat.trendUp ? <FiArrowUp /> : <FiArrowDown />}
                  {stat.trend}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          {/* Line Chart */}
          <div className="chart-card glass">
            <div className="chart-header">
              <div className="chart-title">
                <FiTrendingUp />
                <h3>Verification Trends</h3>
              </div>
              <div className="chart-tabs">
                <button 
                  className={`tab-btn ${selectedPeriod === 'week' ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod('week')}
                >
                  Week
                </button>
                <button 
                  className={`tab-btn ${selectedPeriod === 'month' ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod('month')}
                >
                  Month
                </button>
                <button 
                  className={`tab-btn ${selectedPeriod === 'year' ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod('year')}
                >
                  Year
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="verificationGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#667eea" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.4)" />
                <YAxis stroke="rgba(255,255,255,0.4)" />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="verifications" 
                  stroke="#667eea" 
                  strokeWidth={2}
                  fill="url(#verificationGradient)"
                />
                <Line 
                  type="monotone" 
                  dataKey="approved" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="rejected" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  dot={{ fill: '#ef4444', r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color" style={{ background: '#667eea' }}></div>
                <span>Total Verifications</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ background: '#10b981' }}></div>
                <span>Approved</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ background: '#ef4444' }}></div>
                <span>Rejected</span>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="chart-card glass">
            <div className="chart-header">
              <div className="chart-title">
                <FiBarChart2 />
                <h3>Verification Distribution</h3>
              </div>
              <div className="approval-rate">
                <span className="rate-value">{stats.approvalRate}%</span>
                <span className="rate-label">Approval Rate</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              {pieData.map((item, index) => (
                <div key={index} className="pie-legend-item">
                  <div className="pie-legend-color" style={{ background: item.color }}></div>
                  <span>{item.name}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity Section */}
        <div className="activity-section glass">
          <div className="activity-header">
            <div className="activity-title">
              <FiActivity />
              <h3>Recent Verification Activity</h3>
            </div>
            <div className="activity-actions">
              <button className="filter-btn">
                <FiFilter />
                Filter
              </button>
              <button className="export-btn">
                <FiDownload />
                Export
              </button>
            </div>
          </div>
          
          <div className="activity-timeline">
            {recentActivity.map((activity, index) => (
              <motion.div 
                key={activity.id} 
                className="activity-item"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="activity-icon" style={{ background: `${getStatusColor(activity.status)}20`, color: getStatusColor(activity.status) }}>
                  {getStatusIcon(activity.status)}
                </div>
                <div className="activity-content">
                  <div className="activity-info">
                    <div className="activity-user">
                      <span className="user-name">{activity.user}</span>
                      <span className="user-dssn">{activity.dssn}</span>
                    </div>
                    <div className="activity-meta">
                      <span className="institution">{activity.institution}</span>
                      <span className="time">{activity.time}</span>
                    </div>
                  </div>
                  <div className={`activity-status ${activity.status}`}>
                    {activity.status === 'approved' ? '✓ Approved' : activity.status === 'pending' ? '⏳ Pending' : '✗ Rejected'}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="activity-footer">
            <button className="view-all-btn">
              View All Activity
              <FiMoreHorizontal />
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .dashboard-page {
          min-height: 100vh;
          position: relative;
        }

        .dashboard-background {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
        }

        .orb-1 {
          top: -20%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(102, 126, 234, 0.4), transparent);
        }

        .orb-2 {
          bottom: -20%;
          left: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.3), transparent);
        }

        .orb-3 {
          top: 40%;
          left: 30%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.2), transparent);
        }

        .grid-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        .dashboard-container {
          position: relative;
          z-index: 1;
          max-width: 1400px;
          margin: 0 auto;
          padding: 24px 32px;
        }

        .glass {
          background: rgba(20, 20, 35, 0.6);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
        }

        /* Page Header */
        .page-header {
          padding: 28px 32px;
          margin-bottom: 32px;
          border-radius: 28px;
          background: linear-gradient(135deg, rgba(20, 20, 35, 0.7), rgba(20, 20, 35, 0.5));
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 20px;
        }

        .title-section .badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(102, 126, 234, 0.15);
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 16px;
          border: 1px solid rgba(102, 126, 234, 0.3);
          color: #a8b3ff;
        }

        .title-section h1 {
          font-size: 36px;
          font-weight: 700;
          background: linear-gradient(135deg, #fff, #a8b3ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 8px;
        }

        .title-section p {
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
        }

        .header-stats {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .mini-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 40px;
          font-size: 13px;
        }

        .refresh-btn {
          padding: 8px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .refresh-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .spinning {
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          transition: all 0.3s ease;
        }

        .stat-icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
        }

        .stat-info {
          flex: 1;
        }

        .stat-info h3 {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stat-info p {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 4px;
        }

        .stat-subtitle {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.4);
        }

        .stat-trend {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 20px;
          margin-top: 8px;
        }

        .stat-trend.up {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .stat-trend.down {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        /* Charts Row */
        .charts-row {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 24px;
          margin-bottom: 32px;
        }

        .chart-card {
          padding: 24px;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .chart-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .chart-title svg {
          color: #667eea;
          font-size: 20px;
        }

        .chart-title h3 {
          font-size: 16px;
          font-weight: 600;
        }

        .chart-tabs {
          display: flex;
          gap: 8px;
        }

        .tab-btn {
          padding: 6px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab-btn.active {
          background: rgba(102, 126, 234, 0.2);
          border-color: #667eea;
          color: #a8b3ff;
        }

        .approval-rate {
          text-align: center;
        }

        .rate-value {
          display: block;
          font-size: 24px;
          font-weight: 700;
          color: #10b981;
        }

        .rate-label {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.5);
        }

        .chart-legend {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-top: 16px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
        }

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 3px;
        }

        .pie-legend {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-top: 16px;
        }

        .pie-legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
        }

        .pie-legend-color {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }

        .pie-legend-item strong {
          margin-left: 4px;
          color: white;
        }

        /* Custom Tooltip */
        .custom-tooltip {
          background: rgba(20, 20, 35, 0.95);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 10px 14px;
        }

        .tooltip-label {
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 6px;
          color: rgba(255, 255, 255, 0.8);
        }

        .tooltip-value {
          font-size: 11px;
          margin-top: 3px;
        }

        /* Activity Section */
        .activity-section {
          padding: 24px;
        }

        .activity-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .activity-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .activity-title svg {
          color: #667eea;
          font-size: 20px;
        }

        .activity-title h3 {
          font-size: 18px;
          font-weight: 600;
        }

        .activity-actions {
          display: flex;
          gap: 12px;
        }

        .filter-btn, .export-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-btn:hover, .export-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .activity-timeline {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 16px;
          transition: all 0.2s;
        }

        .activity-item:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .activity-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .activity-content {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .activity-info {
          flex: 1;
        }

        .activity-user {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }

        .user-name {
          font-weight: 600;
          font-size: 14px;
        }

        .user-dssn {
          font-family: monospace;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.5);
          background: rgba(0, 0, 0, 0.3);
          padding: 2px 8px;
          border-radius: 6px;
        }

        .activity-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .institution {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
        }

        .time {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.3);
        }

        .activity-status {
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
        }

        .activity-status.approved {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .activity-status.pending {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .activity-status.rejected {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .activity-footer {
          margin-top: 24px;
          text-align: center;
        }

        .view-all-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-all-btn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .charts-row {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 16px;
          }
          
          .title-section h1 {
            font-size: 24px;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .activity-content {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .activity-meta {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
}
